import React, { FC, useMemo } from "react";
import { Button, Dropdown, Segment, Table } from "semantic-ui-react";

import { useQuery } from "@tanstack/react-query";
import * as trialApi from "../../../../apis/trialApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import _ from "lodash";
import styles from "./MetaTagsTable.module.css";
import sharedStyles from "./ProtocolAnalysis.module.css";
import { MetaTagEdit, useProtocolAnalysis, useProtocolAnalysisDispatch } from "./ProtocolAnalysisContext";
import { useSaveMetaTags } from "./queries";
import type { MetaTagsTableProps } from "./types";

const MetaTagsTable: FC<MetaTagsTableProps> = ({ trialInfo, collapseView = false }) => {
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const {
		pending: { selectedTrialIds },
		display: { isEditingMetaTags },
		metaTagEdits,
		hasUnsavedMetaTags,
	} = useProtocolAnalysis();
	const { changeDisplaySettings, editMetaTag, clearMetaTagEdits } = useProtocolAnalysisDispatch();
	const { save, isSaving } = useSaveMetaTags(trialInfo);

	const getOptions = async (clientId: string) => {
		const accessToken = await getTokenSilently();
		const json = trialApi.getTrialMetaTagOptions(clientId, accessToken);
		return json;
	};

	const { data: metaTagOptions } = useQuery({
		queryKey: ["meta tag options", userAuth.currentClientId],
		queryFn: () => getOptions(userAuth.currentClientId!),
		select: (data) => data.data,
		enabled: !!userAuth.currentClientId,
	});

	const dataStatusOptions = useMemo(
		() =>
			metaTagOptions?.trialExclusionStatuses.map((opt) => ({
				key: opt.id,
				text: opt.name,
				value: opt.id,
			})) ?? [],
		[metaTagOptions],
	);

	const dataQualityOptions = useMemo(
		() =>
			metaTagOptions?.trialDataQualities.map((opt) => ({
				key: opt.id,
				text: opt.name,
				value: opt.id,
			})) ?? [],
		[metaTagOptions],
	);

	const responseCategoriesOptions = useMemo(
		() =>
			metaTagOptions?.trialSiteResponses.map((opt) => ({
				key: opt.id,
				text: opt.name,
				value: opt.id,
			})) ?? [],
		[metaTagOptions],
	);

	const editedTrialInfo = useMemo(
		() => _.map(trialInfo, (ti) => ({ ...ti, ...metaTagEdits[ti.trialId] })),
		[trialInfo, metaTagEdits],
	);

	/** Edits a tag, or reverts it when set back to the saved value so unchanged cells are not left dirty. */
	const editTag = (trialId: string, field: keyof MetaTagEdit, value: string) => {
		const saved = _.find(trialInfo, (ti) => ti.trialId === trialId)?.[field];
		editMetaTag(trialId, { [field]: value === saved ? undefined : value });
	};

	return (
		<Segment basic style={{ width: "fit-content", marginTop: 0 }}>
			<div className={styles.metaTagsTableControls}>
				{!isEditingMetaTags && (
					<Button
						content="Edit"
						compact
						icon="pencil"
						labelPosition="left"
						onClick={() => changeDisplaySettings({ isEditingMetaTags: true })}
					/>
				)}

				{isEditingMetaTags && (
					<>
						<Button
							content="Cancel"
							negative
							compact
							icon="trash"
							labelPosition="left"
							onClick={() => {
								clearMetaTagEdits();
								changeDisplaySettings({ isEditingMetaTags: false });
							}}
						/>
						<Button
							content="Save"
							positive
							compact
							icon="check"
							labelPosition="left"
							loading={isSaving}
							disabled={isSaving || !hasUnsavedMetaTags}
							onClick={() => {
								void save().then((saved) => {
									if (saved) changeDisplaySettings({ isEditingMetaTags: false });
								});
							}}
						/>
					</>
				)}
			</div>
			<div style={{ clear: "both" }} />
			<Table
				celled
				fixed
				className={`${styles.metaTagsTable}${isEditingMetaTags ? ` ${styles.editing}` : ""}`}
				style={{ width: "fit-content" }}
			>
				<Table.Row>
					<Table.Cell className={sharedStyles.rowTitle}>Exclusion Status</Table.Cell>
					{editedTrialInfo.reduce<React.ReactNode[]>((acc, ti) => {
						const isChecked = selectedTrialIds?.includes(ti.trialId) ?? true;
						if (!collapseView || isChecked) {
							acc.push(
								<Table.Cell key={"exclusion-status-" + ti.trialId} style={{ width: 152, overflow: "visible" }}>
									<Dropdown
										className={`${styles.tableDropdown}${
											metaTagEdits[ti.trialId]?.trialExclusionStatusId !== undefined ? ` ${styles.dirty}` : ""
										}`}
										options={dataStatusOptions}
										value={ti.trialExclusionStatusId}
										disabled={!isEditingMetaTags}
										onChange={(_e, { value }) => editTag(ti.trialId, "trialExclusionStatusId", value as string)}
									/>
								</Table.Cell>,
							);
						}
						return acc;
					}, [])}
				</Table.Row>

				<Table.Row>
					<Table.Cell className={sharedStyles.rowTitle}>Data Quality</Table.Cell>
					{editedTrialInfo.reduce<React.ReactNode[]>((acc, ti) => {
						const isChecked = selectedTrialIds?.includes(ti.trialId) ?? true;
						if (!collapseView || isChecked) {
							acc.push(
								<Table.Cell key={"data-quality-" + ti.trialId} style={{ width: 152, overflow: "visible" }}>
									<Dropdown
										className={`${styles.tableDropdown}${
											metaTagEdits[ti.trialId]?.trialDataQualityId !== undefined ? ` ${styles.dirty}` : ""
										}`}
										options={dataQualityOptions}
										value={ti.trialDataQualityId}
										disabled={!isEditingMetaTags}
										onChange={(_e, { value }) => editTag(ti.trialId, "trialDataQualityId", value as string)}
									/>
								</Table.Cell>,
							);
						}
						return acc;
					}, [])}
				</Table.Row>

				<Table.Row>
					<Table.Cell className={sharedStyles.rowTitle}>Site Response</Table.Cell>
					{editedTrialInfo.reduce<React.ReactNode[]>((acc, ti) => {
						const isChecked = selectedTrialIds?.includes(ti.trialId) ?? true;
						if (!collapseView || isChecked) {
							acc.push(
								<Table.Cell key={"site-response-" + ti.trialId} style={{ width: 152, overflow: "visible" }}>
									<Dropdown
										className={`${styles.tableDropdown}${
											metaTagEdits[ti.trialId]?.trialSiteResponseId !== undefined ? ` ${styles.dirty}` : ""
										}`}
										options={responseCategoriesOptions}
										value={ti.trialSiteResponseId}
										disabled={!isEditingMetaTags}
										onChange={(_e, { value }) => editTag(ti.trialId, "trialSiteResponseId", value as string)}
									/>
								</Table.Cell>,
							);
						}
						return acc;
					}, [])}
				</Table.Row>
			</Table>
		</Segment>
	);
};

export default MetaTagsTable;
